import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { TrendingDown, DollarSign, XCircle } from 'lucide-react';
interface SlideProblemProps {
  onContinue: () => void;
}
export function SlideProblem({
  onContinue
}: SlideProblemProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600);
    const t2 = setTimeout(() => setStage(2), 1600);
    const t3 = setTimeout(() => setStage(3), 2600);
    const t4 = setTimeout(() => setStage(4), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-707">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-4" data-id="element-708">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-709">
          {t('The Agency Bottleneck', 'Bureau-flaskehalsen')}
        </h2>
        <p className="text-xl text-white/60" data-id="element-710">
          {t('Why legacy brands die earlier than they need to.', 'Hvorfor legacy-brands dør tidligere end de behøver.')}
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-4xl mx-auto w-full space-y-6" data-id="element-711">
        <div className="space-y-6" data-id="element-712">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-713">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-714">
              <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-715" />
              {t('The Cliff', 'Klippen')}
            </h3>
            <p className="text-white/70" data-id="element-716">
              {t('When patents expire, generic competition can cause sales to drop >90% almost overnight. Marketing investment is stopped immediately.', 'Når patenter udløber, kan generisk konkurrence få salget til at falde >90% næsten natten over. Markedsføringsinvesteringer stoppes øjeblikkeligt.')}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          x: stage >= 2 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-717">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-718">
              <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-719" />
              {t('The Cost Trap', 'Omkostningsfælden')}
            </h3>
            <p className="text-white/70" data-id="element-720">
              {t('Traditional agency campaigns are too expensive to justify on declining revenue. The gap between revenue and cost approaches zero.', 'Traditionelle bureaukampagner er for dyre til at retfærdiggøre på faldende omsætning. Kløften mellem omsætning og omkostninger nærmer sig nul.')}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : -20
        }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-6" data-id="element-721">
            <h3 className="text-rose-400 font-bold text-lg mb-2 flex items-center gap-2" data-id="element-722">
              <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" data-id="element-723" />
              {t('Premature Death', 'For tidlig død')}
            </h3>
            <p className="text-white/90" data-id="element-724">
              {t("The real problem isn't that these brands lack value — it's that traditional marketing economics force companies to abandon them.", 'Det virkelige problem er ikke, at disse brands mangler værdi — det er, at traditionel markedsføringsøkonomi tvinger virksomheder til at opgive dem.')}
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 10
      }} className="bg-navy-800/60 border border-navy-700 rounded-xl px-6 py-4 mt-2" data-id="element-725">
          <p className="text-white/50 leading-relaxed text-sm" data-id="element-726">
            <span className="text-teal-400/80 font-medium" data-id="element-727">
              Note on biologics:
            </span>{' '}
            Biosimilar competition erodes revenue more gradually (15-30% per
            year), so marketing continues longer. But eventually the same
            dynamic applies — declining revenue meets fixed marketing costs, and
            attention fades. Both molecule and biologic brands face the same end
            state.
          </p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-728">
        <Button onClick={onContinue} data-id="element-729">{t('Calculator', 'Beregner')}</Button>
      </motion.div>
    </div>;
}